<script>
import AkDropdown from '@components/v2/input/AkDropdown.vue';
import AkCalendar from '@components/v2/input/AkCalendar.vue';
import GaFilterSidebar from '@components/v2/filter/GaFilterSidebar.vue';
import permissionsMixinComputed from '@mixins/permissionsMixinComputed';

export default {
  name: 'GaFilter',
  components: {GaFilterSidebar, AkCalendar, AkDropdown},
  mixins: [permissionsMixinComputed],
  props: {
    filter: {
      type: Object,
    },
    filterComponents: {
      type: Array,
      required: true,
    },
    showCadre: {
      type: Boolean,
      required: false,
      default: true,
    },
    showActiveFilter: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  emits: ['apply', 'reset', 'update:filter', 'close'],
  data() {
    return {
      internalFilter: {},
      sidebarFilter: {},
    };
  },
  methods: {
    showSidebar() {
      this.$refs.filterSidebar.show();
    },
    resetFilters() {
      this.internalFilter = {...this.sidebarFilter};
      const next = Object.keys(this.internalFilter || {}).reduce((acc, key) => {
        acc[key] = this.internalFilter[key].value;
        return acc;
      }, {});

      if (JSON.stringify(next) !== JSON.stringify(this.filter || {})) {
        this.$emit('update:filter', next);
      }
      this.$nextTick(() => this.$emit('reset'));
      this.$emit('close');
    },
    apply() {
      this.internalFilter = {...this.sidebarFilter};
      const next = Object.keys(this.internalFilter || {}).reduce((acc, key) => {
        acc[key] = this.internalFilter[key].value;
        return acc;
      }, {});

      if (JSON.stringify(next) !== JSON.stringify(this.filter || {})) {
        this.$emit('update:filter', next);
      }
      this.$nextTick(() => this.$emit('apply'));
      this.$emit('close');
    },
    removeFilter(filter) {
      this.sidebarFilter[filter] = {value: null, labels: []};
      this.apply();
    },
    getSidebarInternalFilterProperties(property) {
      const res = {};
      const sidebarProperties = this.filterComponents.map((c) => c.filterProperty);
      Object.keys(property).forEach((key) => {
        if (sidebarProperties.includes(key)) res[key] = property[key];
      });
      return res;
    },
    resetSidebarFilters() {
      this.sidebarFilter = {...this.internalFilter};
      this.$emit('close');
    },
    findLabeledComponentByKey(key) {
      return this.filterComponents.find((fc) => fc.filterProperty === key && fc.label);
    },
  },
  computed: {
    nbFiltersApplied() {
      const rest = this.getSidebarInternalFilterProperties(this.filter);
      return Object.keys(rest).reduce((count, key) => {
        const fc = this.filterComponents.find((c) => c.filterProperty === key);
        if (fc?.hideFromCount) return count;
        const v = rest[key];
        if (v === null || v === undefined) return count;
        if (Array.isArray(v)) return v.length > 0 ? count + 1 : count;
        return count;
      }, 0);
    },
    appliedFilters() {
      const rest = this.getSidebarInternalFilterProperties(this.internalFilter);
      const res = [];
      Object.keys(rest).forEach((key) => {
        const fc = this.findLabeledComponentByKey(key);
        if (!fc) return;
        if (fc.hideFromCount) return;
        res.push({
          key: key,
          label: fc.label,
          values: rest[key].labels,
        });
      });
      return res.filter((f) => f.values.length > 0);
    },
  },
  watch: {
    filter: {
      handler(newVal) {
        const source = newVal && typeof newVal === 'object' ? newVal : {};

        Object.keys(this.internalFilter).forEach((key) => {
          if (!Object.keys(source).includes(key)) this.internalFilter[key].value = undefined; // Remove old properties that aren't in the new filter anymore
        });

        Object.keys(source).forEach((key) => {
          if (!this.internalFilter[key]) {
            this.internalFilter = {
              ...this.internalFilter,
              [key]: {value: source[key], labels: []},
            };
          } else {
            this.internalFilter[key].value = source[key];
          }
        });

        this.sidebarFilter = {...this.internalFilter};
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>

<template>
  <div class="rounded-xl w-full flex flex-col gap-4 border-grey-100" :class="{'md:border md:p-6': showCadre}">
    <div class="flex gap-6 w-full justify-end" id="filterSlot">
      <slot name="inlineFilters" :internalFilter="this.internalFilter" />
      <button
        @click="showSidebar"
        id="filterButton"
        class="h-[48px] self-end ml-auto md:ml-0 flex gap-2 rounded-md items-center border border-grey-100 py-2 pl-4 pr-4 font-medium text-grey-900 hover:bg-grey-50 hover:border-grey-200"
        tabindex="0">
        Filtres
        <div
          v-if="nbFiltersApplied"
          id="filterButtonAppliedCount"
          class="rounded-full -mr-1 font-medium text-base text-[#ECF7FE] bg-primary flex items-center justify-center w-[24px] h-[24px] select-none">
          {{ nbFiltersApplied }}
        </div>
        <i class="ga-icon ga-filter text-2xl -mr-1" />
      </button>
    </div>
    <div v-if="appliedFilters.length && showActiveFilter" class="flex flex-wrap w-full gap-2 justify-end">
      <div
        class="flex gap-2 pr-2 pl-2 py-2 rounded-full items-center text-grey-900 bg-secondary-50 border border-secondary"
        v-for="f in appliedFilters"
        :key="f.key">
        <span class="font-medium text-primary" v-if="f.values.length === 1">
          {{ f.values[0] }}
        </span>
        <template v-else>
          <div
            class="rounded-full w-[21px] h-[21px] flex items-center justify-center bg-primary text-white font-medium">
            {{ f.values.length }}
          </div>
          <span class="font-medium text-primary">
            {{ f.label }}
          </span>
        </template>
        <div
          @click="removeFilter(f.key)"
          class="flex items-center justify-center rounded-full bg-secondary-500 w-[21px] h-[21px] hover:bg-primary relative">
          <i class="ga-icon ga-supprimer text-white text-lg font-bold cursor-pointer absolute left-[1.5px]" />
        </div>
      </div>
    </div>
  </div>

  <GaFilterSidebar
    ref="filterSidebar"
    v-model:filter="sidebarFilter"
    :filter-components="filterComponents"
    @apply="apply"
    @reset="resetFilters"
    @cancel="resetSidebarFilters" />
</template>

<style scoped></style>
